/**
 * DefaultVariant — Cart Summary V2
 *
 * Renders the standard cart summary layout:
 *   - Subtotal row: label + compare-at (strikethrough) + actual total
 *   - Savings row: green branded "YOU'RE SAVING" with amount
 *   - Error message (when present)
 *   - Full-width checkout CTA
 */

'use client';

import { Bookmark, Lock } from 'lucide-react';
import { formatPrice } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n';
import {
  CART_SUMMARY_CHECKOUT,
  CART_SUMMARY_SAVINGS_LABEL,
  CART_SUMMARY_SUBTOTAL,
} from './i18.keys';
import {
  SummaryContainer,
  Row,
  RowPrices,
  SubtotalLabel,
  CompareAtPrice,
  TotalPrice,
  SavingsRow,
  SavingsLabel,
  SavingsValue,
  SavingsIcon,
  CheckoutButton,
  ErrorMessage,
} from '../CartSummaryV2.styled';
import type { VariantProps } from '../types';
import { DiscountBreakdownList } from '../DiscountBreakdownList';

export function DefaultVariant({
  total,
  compareAtTotal,
  savings,
  appliedDiscountRows,
  onCheckout,
  isCheckoutDisabled,
  error,
  priceOptions,
  offerFooter,
  menuFooter,
}: Readonly<VariantProps>) {
  const { t } = useTranslation('sidecart');

  const showCompareAt = compareAtTotal != null && compareAtTotal > total;

  return (
    <>
      {offerFooter}

      <SummaryContainer>
        <Row>
          <SubtotalLabel variant="bodySmall" weight="semibold" as="span">
            {t(CART_SUMMARY_SUBTOTAL)}
          </SubtotalLabel>
          <RowPrices>
            {showCompareAt && (
              <CompareAtPrice variant="bodySmall" as="span" data-testid="compare-at-price">
                {formatPrice(compareAtTotal, priceOptions)}
              </CompareAtPrice>
            )}
            <TotalPrice variant="body" weight="bold" as="span" data-testid="subtotal-price">
              {formatPrice(total, priceOptions)}
            </TotalPrice>
          </RowPrices>
        </Row>

        {appliedDiscountRows && appliedDiscountRows.length > 0 && (
          <DiscountBreakdownList discounts={appliedDiscountRows} priceOptions={priceOptions} />
        )}

        {savings > 0 && (
          <SavingsRow>
            <SavingsLabel variant="labelCaps" as="span">
              <SavingsIcon>
                <Bookmark size={18} aria-hidden />
              </SavingsIcon>
              {t(CART_SUMMARY_SAVINGS_LABEL)}
            </SavingsLabel>
            <SavingsValue variant="bodySmall" weight="bold" as="span" data-testid="savings-price">
              {formatPrice(savings, priceOptions)}
            </SavingsValue>
          </SavingsRow>
        )}
      </SummaryContainer>

      {error && (
        <ErrorMessage variant="error">
          {String(error)}
        </ErrorMessage>
      )}

      <CheckoutButton
        variant="secondary"
        fullWidth
        disabled={isCheckoutDisabled}
        onClick={onCheckout}
        leftIcon={<Lock size={18} aria-hidden />}
        testId="checkout"
      >
        {t(CART_SUMMARY_CHECKOUT)}
      </CheckoutButton>

      {menuFooter}
    </>
  );
}
